// The ledger panel of SCR-007 (API-022): a closed period's frozen invoice or an open period's live
// preview — the metered usage at AWS list price, its discrepancies, and the CSV download. The
// customer invoice page (screens/invoice/customer-invoice.tsx) composes it under the customer
// invoice and statement.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ErrorBanner, Loading } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import { ledgerInvoiceView, type Discrepancy, type Invoice, type InvoiceLine } from "./model";

export interface LedgerInvoiceProps {
  orgId: string;
  period: string;
}

interface FetchState {
  loading: boolean;
  invoice: Invoice | null;
  error: ApiError | null;
}

const KIND_TEXT: Record<string, string> = { usage: "Usage", late_after_close: "Late (after close)" };

const LINE_COLUMNS: Column<InvoiceLine>[] = [
  { key: "kind", header: "Line", cell: (l) => KIND_TEXT[l.kind] ?? l.kind },
  { key: "model", header: "Model", cell: (l) => <span class="mono">{l.bedrockModelId}</span> },
  { key: "class", header: "Class", cell: (l) => l.tokenClass },
  { key: "tokens", header: "Tokens", numeric: true, cell: (l) => l.tokens.toLocaleString("en-US") },
  { key: "rate", header: "Rate per 1M", numeric: true, help: "terms.ledgerRate", cell: (l) => (l.ratePerM === null ? "—" : <Money micro={l.ratePerM} />) },
  {
    key: "verified",
    header: "Price verified",
    help: "terms.priceVerified",
    cell: (l) => (l.priceVerified === false ? <Chip tone="amber">Unverified</Chip> : l.priceVerified === null ? "—" : "Verified"),
  },
  { key: "amount", header: "Amount", numeric: true, cell: (l) => (l.amountMicroUsd === null ? "—" : <Money micro={l.amountMicroUsd} />) },
];

const DISCREPANCY_COLUMNS: Column<Discrepancy>[] = [
  { key: "kind", header: "Kind", cell: (d) => d.kind },
  { key: "quantity", header: "Quantity", numeric: true, cell: (d) => d.quantity.toLocaleString("en-US") },
  { key: "detail", header: "Detail", cell: (d) => (d.detail ? <span class="mono">{JSON.stringify(d.detail)}</span> : "—") },
];

export function LedgerInvoice({ orgId, period }: LedgerInvoiceProps) {
  const client = useApi();
  const [state, setState] = useState<FetchState>({ loading: true, invoice: null, error: null });
  const [token, setToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, invoice: null, error: null });
    client
      .get("/v1/orgs/{orgId}/billing-periods/{period}/invoice", { path: { orgId, period } })
      .then((invoice) => {
        if (!cancelled) setState({ loading: false, invoice, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setState({ loading: false, invoice: null, error });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [client, orgId, period, token]);

  const view = state.invoice ? ledgerInvoiceView(state.invoice) : null;
  const csvHref = client.csvUrl("/v1/orgs/{orgId}/billing-periods/{period}/invoice", { path: { orgId, period } });

  return (
    <Card
      title="Ledger invoice"
      help="terms.ledgerInvoice"
      class="panel-ledger-invoice"
      actions={view && <Chip tone={view.final ? "grey" : "blue"}>{view.badge}</Chip>}
    >
      {state.loading && <Loading label="Loading ledger invoice…" />}
      {state.error && <ErrorBanner error={state.error} onRetry={() => setToken((t) => t + 1)} />}
      {view && (
        <>
          <p class="card-note">The metered usage at AWS list price, before the rate plan: what the month cost at list price.</p>
          {view.forced && <p class="banner banner-warning">Forced close{view.forceReason ? `: ${view.forceReason}` : "."}</p>}
          {view.unpricedModels.length > 0 && <p class="banner banner-warning">Unpriced models: {view.unpricedModels.join(", ")}</p>}
          <dl class="summary">
            <div>
              <dt>Usage</dt>
              <dd>{view.usageMicroUsd === null ? "—" : <Money micro={view.usageMicroUsd} />}</dd>
            </div>
            <div>
              <dt>Late usage carried in</dt>
              <dd>{view.adjustmentsMicroUsd === null ? "—" : <Money micro={view.adjustmentsMicroUsd} />}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>{view.totalMicroUsd === null ? "—" : <Money micro={view.totalMicroUsd} />}</dd>
            </div>
          </dl>
          <DataTable
            caption="Invoice lines"
            columns={LINE_COLUMNS}
            rows={view.lines}
            rowKey={(l) => String(l.lineNo)}
            emptyMessage="No invoice lines."
            csvHref={csvHref}
          />
          <DataTable
            caption="Discrepancies"
            columns={DISCREPANCY_COLUMNS}
            rows={view.discrepancies}
            rowKey={(d) => String(d.lineNo)}
            emptyMessage="No discrepancies."
          />
        </>
      )}
    </Card>
  );
}
